// Nishi-Shinjuku west (scene "nishi-west"): station hall, corporate glass towers, office plazas and loading podiums.
// Colder than Kabukicho: glass and pale concrete, cyan/magenta corporate signage, few warm accents.
// Kind, roof and prop names are prefixed "nishiWest". Signs use the kit's katakana plus a few extra glyphs kept here.
import { hash2 } from '../../common/src/util';
import { K, NEON, type NeonColor, type G, px, speckle, dither, bands, disc, frame, shadow, dk, lt, mix, alpha, text, textW } from './hd-kit';
import { registerFront, registerRoof, registerRoofProp, type LightFn } from './hd-registry';
import { door, display, windowPane, plant, vending } from './hd-parts';

const gTextW = textW, gText = text;
/** glyph scale 3 when the text fits `w`, else 2, else 1 */
const fit = (t: string, w: number) => (gTextW(t, 3) + 14 <= w ? 3 : gTextW(t, 2) + 14 <= w ? 2 : 1);

/** a lit corporate sign panel (cold: dark tinted plate, bright rim, light text) */
function plate(g: G, cx: number, y: number, txt: string, color: NeonColor, L: LightFn, s: number) {
  const col = NEON[color], w = gTextW(txt, s) + 14, h = 6 * s + 10, x = cx - Math.floor(w / 2);
  px(g, x, y, w, h, K.ink); px(g, x + 1, y + 1, w - 2, h - 2, dk(col, 0.86));
  frame(g, x + 2, y + 2, w - 4, h - 4, col);
  gText(g, txt, x + 7, y + 5, lt(col, 0.65), s);
  L(x, y, w, h, col, 0.55, true, true);
  return w;
}

// ---------------------------------------------------------------- roofs
registerRoof('nishiWestPlate', { paint(g, x, y, w, h, seed) {
  px(g, x, y, w, h, mix(K.concrete, K.concreteHi, 0.3));
  for (let j = 0; j < h; j += 48) for (let i = 0; i < w; i += 48) {
    const v = hash2(x + i + seed, y + j);
    if (v < 0.25) px(g, x + i, y + j, Math.min(48, w - i), Math.min(48, h - j), mix(K.concrete, K.concreteLo, 0.4));
    px(g, x + i, y + j, Math.min(48, w - i), 1, K.concreteLo); px(g, x + i, y + j, 1, Math.min(48, h - j), K.concreteLo);
  }
  speckle(g, x, y, w, h, K.concreteLo, 0.02, 41, 2);
  for (let k = 0; k < Math.floor(w * h / 14000); k++) { // a thin film of rain tinted by the cyan sky
    const a = x + 16 + Math.floor(hash2(seed, k + 9) * (w - 32)), b = y + 14 + Math.floor(hash2(k + 9, seed) * Math.max(1, h - 28));
    disc(g, a, b, 9, 3, mix(K.concrete, K.glass, 0.4));
  }
} });
registerRoof('nishiWestGlass', { paint(g, x, y, w, h, seed) {
  px(g, x, y, w, h, K.glassLo);
  for (let j = 8; j < h - 8; j += 24) for (let i = 8; i < w - 8; i += 32) {
    const v = hash2(x + i, y + j + seed);
    px(g, x + i, y + j, 30, 22, v < 0.3 ? K.glass : v < 0.5 ? mix(K.glass, K.glassHi, 0.4) : mix(K.glassLo, K.glass, 0.5));
    px(g, x + i, y + j, 30, 1, K.glassHi); px(g, x + i + 2, y + j + 2, 8, 1, alpha(K.white, 0.25));
  }
  for (let i = 8; i < w - 8; i += 32) px(g, x + i - 2, y, 2, h, K.metalLo);
  for (let j = 8; j < h - 8; j += 24) px(g, x, y + j - 2, w, 2, K.metalLo);
  for (let k = 0; k < h; k += 2) px(g, x + (k * 3) % Math.max(1, w), y + k, 3, 1, alpha(K.white, 0.08)); // reflection streak
} });

// ---------------------------------------------------------------- roof props
registerRoofProp('nishiWestHeli', ({ g, x, y, L }) => {
  shadow(g, x + 4, y + 6, 76, 56);
  disc(g, x + 36, y + 28, 36, 26, K.ink); disc(g, x + 36, y + 28, 34, 24, K.metalLo); disc(g, x + 36, y + 28, 30, 21, K.concrete);
  for (let i = 0; i < 12; i++) { const a = i / 12 * Math.PI * 2; px(g, x + 36 + Math.round(Math.cos(a) * 32) - 1, y + 28 + Math.round(Math.sin(a) * 23) - 1, 3, 3, K.warmHi); }
  px(g, x + 22, y + 18, 3, 20, K.white); px(g, x + 47, y + 18, 3, 20, K.white); px(g, x + 22, y + 27, 28, 3, K.white);
  L(x + 8, y + 8, 56, 40, K.cyan, 0.12);
});
registerRoofProp('nishiWestChiller', ({ g, x, y }) => {
  shadow(g, x + 5, y + 6, 72, 40);
  px(g, x, y, 72, 40, K.ink); px(g, x + 1, y + 1, 70, 20, K.metalHi); px(g, x + 1, y + 21, 70, 18, K.metal);
  for (let i = 0; i < 3; i++) { disc(g, x + 12 + i * 24, y + 11, 9, 7, K.metalLo); disc(g, x + 12 + i * 24, y + 11, 6, 4, K.ink); px(g, x + 3 + i * 24, y + 11, 18, 1, K.metal); px(g, x + 12 + i * 24, y + 3, 1, 16, K.metal); }
  for (let j = 24; j < 38; j += 3) px(g, x + 2, y + j, 68, 1, K.metalLo);
});
registerRoofProp('nishiWestMast', ({ g, x, y, L }) => {
  shadow(g, x + 3, y + 4, 10, 8);
  px(g, x + 3, y + 8, 6, 6, K.ink); px(g, x + 4, y + 9, 4, 4, K.metalHi);
  px(g, x + 5, y - 28, 2, 36, K.ink); px(g, x + 1, y - 18, 10, 1, K.ink); px(g, x + 2, y - 10, 8, 1, K.ink);
  px(g, x + 4, y - 31, 4, 4, K.red); L(x - 2, y - 34, 16, 10, K.pink, 0.7, false, true);
});
registerRoofProp('nishiWestSolar', ({ g, x, y }) => {
  shadow(g, x + 4, y + 5, 64, 30);
  px(g, x, y, 64, 30, K.ink);
  for (let j = 0; j < 2; j++) for (let i = 0; i < 4; i++) { px(g, x + 2 + i * 15, y + 2 + j * 14, 14, 13, K.glassLo); px(g, x + 3 + i * 15, y + 3 + j * 14, 12, 3, K.glass); px(g, x + 3 + i * 15, y + 3 + j * 14, 4, 1, K.glassHi); for (let k = 6; k < 14; k += 3) px(g, x + 3 + i * 15, y + 2 + j * 14 + k, 12, 1, K.glass); }
});

// ---------------------------------------------------------------- shared painters
/** glass curtain wall between y0 and y1; lit cells are mostly cold, with the odd warm office or magenta lounge */
function curtain(g: G, x: number, y0: number, y1: number, w: number, seed: number, L: LightFn) {
  for (let fy = y0; fy + 22 <= y1; fy += 26) {
    px(g, x + 4, fy + 22, w - 8, 3, K.concreteLo); px(g, x + 4, fy + 22, w - 8, 1, K.concreteHi);
    for (let i = x + 6; i + 14 <= x + w - 6; i += 18) {
      const v = hash2(i + seed, fy), lit = v < 0.42;
      px(g, i - 1, fy - 1, 16, 24, K.ink);
      if (lit) {
        const tone = v < 0.06 ? K.pink : v < 0.34 ? K.cyan : K.amber;
        bands(g, i, fy, 14, 22, lt(tone, 0.55), dk(tone, 0.3), 3); L(i, fy, 14, 22, tone, 0.1);
      } else { px(g, i, fy, 14, 22, K.glassLo); px(g, i, fy, 14, 6, K.glass); px(g, i + 1, fy + 1, 5, 1, K.glassHi); }
      px(g, i + 7, fy, 1, 22, K.metalLo);
    }
  }
  for (let k = 0; k < y1 - y0; k += 2) px(g, x + 10 + Math.floor(k * 1.4) % Math.max(1, w - 24), y0 + k, 3, 1, alpha(K.white, 0.09)); // sky glint
}
/** stainless entrance canopy with a lit cyan edge */
function canopy(g: G, x: number, y: number, w: number, L: LightFn, tone: string = K.cyan) {
  px(g, x, y, w, 6, K.ink); px(g, x + 1, y + 1, w - 2, 2, K.concreteHi); px(g, x + 1, y + 3, w - 2, 2, K.metalLo); px(g, x, y + 6, w, 3, alpha(K.night, 0.5));
  px(g, x + 2, y + 5, w - 4, 1, lt(tone, 0.4)); L(x, y + 4, w, 4, tone, 0.45, true);
}

// ---------------------------------------------------------------- fronts
registerFront('nishiWestTower', { wall: dk(K.slate, 0.55), upper: 'none' }, ({ g, x, y, w, gf, gy, seed, sign, L }) => {
  curtain(g, x, y + 12, gy - 6, w, seed, L);
  if (sign) plate(g, x + w / 2, y + 14, sign.text, sign.color, L, fit(sign.text, w - 12));
  canopy(g, x + 14, gy - 5, w - 28, L);
  const cx = x + w / 2, dw = Math.min(64, w - 80);
  door(g, cx - dw / 2, gy + 5, dw, gf - 7, K.cyan, L);
  display(g, x + 20, gy + 9, Math.max(14, (w - dw) / 2 - 44), gf - 16, K.cyan, L, false);
  display(g, x + w - 20 - Math.max(14, (w - dw) / 2 - 44), gy + 9, Math.max(14, (w - dw) / 2 - 44), gf - 16, K.cyan, L, false);
  plant(g, cx - dw / 2 - 18, gy + gf - 20); plant(g, cx + dw / 2 + 8, gy + gf - 20);
});

registerFront('nishiWestStation', { wall: mix(K.wallHi, K.plasterLo, 0.2), upper: 'none' }, ({ g, x, y, w, h, gf, gy, sign, L }) => {
  // a pale concrete hall with a band of glass, the station name over it and a cyan/amber line stripe
  px(g, x + 4, y + 12, w - 8, 3, K.cyan); px(g, x + 4, y + 15, w - 8, 1, K.amber); L(x + 4, y + 12, w - 8, 3, K.cyan, 0.4, true);
  if (sign) plate(g, x + w / 2, y + 20, sign.text, sign.color, L, fit(sign.text, w - 12));
  const top = y + 20 + 6 * fit(sign?.text ?? '', w - 12) + 14;
  if (gy - top > 16) { // clerestory
    px(g, x + 8, top, w - 16, gy - top - 6, K.ink);
    for (let i = x + 10; i + 18 <= x + w - 10; i += 20) { bands(g, i, top + 2, 18, gy - top - 10, K.glassHi, K.glass, 3); px(g, i + 9, top + 2, 1, gy - top - 10, K.glassLo); L(i, top + 2, 18, gy - top - 10, K.cyan, 0.08); }
  }
  canopy(g, x + 8, gy - 5, w - 16, L, K.amber);
  const n = Math.max(1, Math.floor((w - 32) / 150));
  for (let k = 0; k < n; k++) { // gate bays: a lit opening with ticket gates inside and a timetable board
    const bx = x + 16 + k * ((w - 32) / n) + 8, bw = (w - 32) / n - 16;
    px(g, bx - 2, gy + 4, bw + 4, gf - 5, K.ink); bands(g, bx, gy + 6, bw, gf - 10, lt(K.cyan, 0.5), dk(K.cyan, 0.4), 3);
    for (let i = bx + 6; i + 10 < bx + bw - 6; i += 18) { px(g, i, gy + gf - 20, 10, 14, K.ink); px(g, i + 1, gy + gf - 19, 8, 3, K.cyan); px(g, i + 1, gy + gf - 12, 8, 1, K.amber); }
    L(bx, gy + 6, bw, gf - 10, K.cyan, 0.4, true);
    if (bw > 80) { px(g, bx + bw / 2 - 20, gy + 8, 40, 10, K.ink); for (let j = 0; j < 3; j++) px(g, bx + bw / 2 - 17, gy + 10 + j * 3, 8 + Math.floor(hash2(k, j) * 22), 1, K.warmHi); }
  }
  vending(g, x + 4, gy + gf - 40, dk(K.cyan, 0.45), L);
});

registerFront('nishiWestMall', { wall: dk(K.slate, 0.5), upper: 'none' }, ({ g, x, y, w, h, gf, gy, seed, sign, L }) => {
  // arcade of bright narrow shops under a glass gallery
  curtain(g, x, y + 12, gy - 14, w, seed + 5, L);
  px(g, x + 2, gy - 12, w - 4, 8, K.ink); px(g, x + 3, gy - 11, w - 6, 3, dk(K.pink, 0.4)); px(g, x + 3, gy - 8, w - 6, 1, K.pink); L(x + 4, gy - 11, w - 8, 4, K.pink, 0.4, true);
  if (sign) plate(g, x + w / 2, y + 12, sign.text, sign.color, L, fit(sign.text, w - 12));
  const cell = 76, n = Math.max(1, Math.floor(w / cell));
  for (let k = 0; k < n; k++) {
    const bx = x + 8 + k * ((w - 16) / n), bw = (w - 16) / n - 8, tone = [K.cyan, K.pink, K.amber][k % 3];
    display(g, bx, gy + 10, bw, gf - 16, tone, L, k % 2 === 0);
    for (let i = 0; i < bw; i += 8) px(g, bx + i, gy + 2, 4, 7, dk(tone, 0.4)), px(g, bx + i + 4, gy + 2, 4, 7, lt(tone, 0.2));
  }
});

registerFront('nishiWestAtrium', { wall: dk(K.cyan, 0.9), upper: 'none' }, ({ g, x, y, w, h, gf, gy, seed, sign, L }) => {
  // double-height glass atrium: warm lobby light behind cold mullions
  const ax = x + 14, aw = w - 28, ay = y + 16, ah = h - 24;
  px(g, ax - 2, ay - 2, aw + 4, ah + 3, K.ink);
  bands(g, ax, ay, aw, ah, dk(K.amber, 0.25), dk(K.amber, 0.55), 5);
  for (let i = ax + 20; i < ax + aw - 10; i += 38) for (let j = ay + 8; j < ay + ah - 20; j += 16) px(g, i, j, 8, 2, alpha(K.warmHi, 0.6));
  for (let i = ax + 14; i < ax + aw; i += 28) { px(g, i, ay + ah - 26, 3, 22, K.metalLo); disc(g, i + 1, ay + ah - 32, 10, 8, K.leafLo); disc(g, i, ay + ah - 34, 7, 6, K.leaf); }
  for (let i = ax + 28; i < ax + aw; i += 28) px(g, i, ay, 2, ah, K.metalLo);
  px(g, ax, ay + 18, aw, 2, K.metalLo);
  for (let k = 0; k < ah; k += 2) px(g, ax + 6 + Math.floor(k * 1.6) % Math.max(1, aw - 12), ay + k, 3, 1, alpha(K.white, 0.14));
  L(ax, ay, aw, ah, K.amber, 0.4, true);
  if (sign) plate(g, x + w / 2, y + 2, sign.text, sign.color, L, fit(sign.text, w - 12));
  door(g, x + w / 2 - 22, gy + 6, 44, gf - 8, K.cyan, L);
});

registerFront('nishiWestPodium', { wall: K.wall, upper: 'none' }, ({ g, x, y, w, h, gf, gy, seed, sign, L }) => {
  // loading dock and car park: shutters, amber hazard stripes, a lit sign
  if (sign) plate(g, w < 200 ? x + w / 2 : x + 60, y + 3, sign.text, sign.color, L, 1);
  const bays = Math.max(1, Math.floor(w / 110));
  for (let k = 0; k < bays; k++) {
    const bx = x + 10 + k * ((w - 20) / bays), bw = (w - 20) / bays - 14;
    px(g, bx - 2, gy + 2, bw + 4, gf - 3, K.ink);
    const open = hash2(seed + k, 3) < 0.4, sh = open ? 8 : gf - 8;
    for (let j = 0; j < sh; j += 3) { px(g, bx, gy + 4 + j, bw, 2, K.metal); px(g, bx, gy + 6 + j, bw, 1, K.metalLo); }
    if (open) { px(g, bx, gy + 14, bw, gf - 18, K.glassLo); px(g, bx, gy + gf - 10, bw, 3, K.warmHi); L(bx, gy + gf - 11, bw, 4, K.amber, 0.35, true); }
    for (let i = 0; i < bw; i += 8) px(g, bx + i, gy - 2, 4, 3, K.warm), px(g, bx + i + 4, gy - 2, 4, 3, K.ink);
    if (hash2(k, seed) < 0.5) px(g, bx + 6, gy + 16, 14, 4, alpha(K.pink, 0.35));
  }
  px(g, x + 4, gy - 5, 8, 3, K.warmHi); L(x + 2, gy - 6, 14, 5, K.amber, 0.3, true);
});
